/** Shared helper for GPU-shader identity short-circuits.
 *
 *  Several GPU shaders implement their CPU counterpart's identity
 *  short-circuit (`if (amount === 0) return source copy`) by
 *  detouring through canvas2D rather than invoking the shader at
 *  all — saves a texture upload + drawImage for zero-effect work.
 *
 *  This helper is the single implementation of that copy step.
 *  Returns `true` on success (target now holds the source pixels)
 *  or `false` when the target's 2D context is unavailable — in
 *  which case the caller should fall through to the slow path or
 *  the wider CPU dispatch. Callers should NOT use this for any
 *  case other than the identity short-circuit; the regular shader
 *  runtime already handles resize + render correctly. */
export function applyIdentityCopy(source: ImageData, target: HTMLCanvasElement): boolean {
  if (target.width !== source.width) target.width = source.width;
  if (target.height !== source.height) target.height = source.height;
  const ctx = target.getContext('2d');
  if (!ctx) return false;
  ctx.putImageData(source, 0, 0);
  return true;
}
